/**
 * AutoBpmDialog — the shared "Auto BPM" popup (the metronome animation + result
 * stages). Presentational only: each editor drives it with its own detection
 * state. Flow uses the full flow (detecting → verifying online → choose → done);
 * VR / Console use a simpler detect → done flow — the dialog renders whatever
 * stage/values it's given, so the extra stages just don't appear for them.
 */

import React from 'react';
import { color, font } from './theme';
import MetronomeIcon from './MetronomeIcon';

export type BpmStage = null | 'detecting' | 'verifying' | 'choose' | 'done';

export interface AutoBpmDialogProps {
  stage: BpmStage;
  detected: number | null;
  final: number | null;
  online?: number | null;
  onlineNote?: string;
  confidence?: number | null;
  candidates?: { bpm: number; strength: number }[];
  reconciled?: string;
  onChoose: (bpm: number) => void;
  onClose: () => void;
}

// Nearby "round" tempos to offer as one-click fixes (most tracks are integer or
// .5 BPM, and a real-audio estimate can sit ~0.5 off).
function roundNeighbors(bpm: number): number[] {
  if (!bpm) return [];
  const cands = [Math.floor(bpm), Math.ceil(bpm), Math.round(bpm * 2) / 2];
  const seen = new Set<number>();
  return cands
    .filter(v => v > 0 && Math.abs(v - bpm) > 1e-6 && !seen.has(v) && (seen.add(v), true))
    .sort((a, b) => Math.abs(a - bpm) - Math.abs(b - bpm));
}

export default function AutoBpmDialog({
  stage, detected, final, online, onlineNote, confidence, candidates = [], reconciled, onChoose, onClose,
}: AutoBpmDialogProps) {
  if (!stage) return null;
  const busy = stage === 'detecting' || stage === 'verifying';
  return (
    <div style={backdrop} onClick={() => !busy && onClose()}>
      <div style={card} onClick={e => e.stopPropagation()}>
        {stage === 'detecting' && (
          <>
            <div style={{ ...spinner, color: 'var(--accent)' }}><MetronomeIcon size={40} animate /></div>
            <div style={title}>Calculating BPM…</div>
          </>
        )}

        {stage === 'verifying' && (
          <>
            <div style={{ ...spinner, color: 'var(--accent)' }}><MetronomeIcon size={40} animate /></div>
            <div style={title}>Detected BPM: {detected}</div>
            <div style={sub}>Verifying online…</div>
          </>
        )}

        {stage === 'choose' && (
          <>
            <div style={title}>Online BPM doesn't match</div>
            <div style={sub}>
              Detected <b style={{ color: color.ink }}>{detected}</b>
              {confidence != null && (
                <span style={{ color: confidence < 0.5 ? '#F59E0B' : '#9B8CFF' }}>
                  {' '}({Math.round(confidence * 100)}% confidence{confidence < 0.5 ? ' — octave ambiguous' : ''})
                </span>
              )}
              {' '}· Online <b style={{ color: '#22D3EE' }}>{online}</b>
              {onlineNote ? ` — ${onlineNote}` : ''}
            </div>
            <div style={sub2}>Which one do you want to use?</div>
            <div style={row}>
              <button style={applyBtn} onClick={() => onChoose(detected as number)}>Use {detected}</button>
              <button style={{ ...applyBtn, borderColor: 'rgba(34,211,238,0.5)', color: '#22D3EE' }} onClick={() => onChoose(online as number)}>Use {online}</button>
            </div>
            {roundNeighbors(detected ?? 0).length > 0 && (
              <>
                <div style={{ ...sub2, marginTop: 10, fontSize: 11 }}>Round to</div>
                <div style={row}>
                  {roundNeighbors(detected ?? 0).map(v => (
                    <button key={v} style={{ ...applyBtn, padding: '8px 14px', fontSize: 13 }} onClick={() => onChoose(v)}>{v}</button>
                  ))}
                </div>
              </>
            )}
            {candidates.filter(c => Math.abs(c.bpm - (detected ?? 0)) > 0.5).length > 0 && (
              <>
                <div style={{ ...sub2, marginTop: 10, fontSize: 11 }}>Other octaves</div>
                <div style={row}>
                  {candidates.filter(c => Math.abs(c.bpm - (detected ?? 0)) > 0.5).map(c => (
                    <button key={c.bpm} style={{ ...applyBtn, padding: '8px 14px', fontSize: 13 }} onClick={() => onChoose(c.bpm)}>{c.bpm}</button>
                  ))}
                </div>
              </>
            )}
            <button style={cancelBtn} onClick={onClose}>Cancel — keep current BPM</button>
          </>
        )}

        {stage === 'done' && (
          <>
            <div style={title}>Final BPM: {final}</div>
            <div style={sub}>
              {reconciled
                ? `✓ ${reconciled}`
                : online == null
                  ? 'Detected from the audio'
                  : '✓ verified'}
            </div>
            {final != null && Math.abs(final - Math.round(final)) > 1e-6 && roundNeighbors(final).length > 0 && (
              <>
                <div style={{ ...sub2, marginTop: 10, fontSize: 11 }}>Fine-tune to a round BPM</div>
                <div style={row}>
                  {roundNeighbors(final).map(v => (
                    <button key={v} style={{ ...applyBtn, padding: '8px 14px', fontSize: 13 }} onClick={() => onChoose(v)}>{v}</button>
                  ))}
                </div>
                <button style={cancelBtn} onClick={onClose}>Keep {final}</button>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}

const backdrop: React.CSSProperties = {
  position: 'fixed', inset: 0, zIndex: 10000,
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  background: 'oklch(0 0 0 / 0.55)', backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)',
};
const card: React.CSSProperties = {
  minWidth: 'min(300px, calc(100vw - 24px))', maxWidth: 'min(420px, calc(100vw - 24px))',
  maxHeight: 'calc(100vh - 24px)', overflowY: 'auto', padding: '26px 28px', borderRadius: 18,
  background: 'oklch(0.11 calc(0.02 * var(--tint-c)) var(--tint-h) / 0.72)',
  border: '1px solid oklch(0.70 calc(0.205 * var(--accent-c)) var(--accent-h) / 0.25)',
  boxShadow: '0 12px 48px rgba(0,0,0,0.6), 0 0 30px oklch(0.55 calc(0.16 * var(--accent-c)) var(--accent-h) / 0.10)',
  backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)',
  textAlign: 'center', color: color.ink, fontFamily: font.sans,
};
const spinner: React.CSSProperties = { fontSize: 30, marginBottom: 10 };
const title: React.CSSProperties = { fontSize: 17, fontWeight: 700, letterSpacing: 0.3 };
const sub: React.CSSProperties = { marginTop: 8, fontSize: 12, color: color.inkDim, lineHeight: 1.4 };
const sub2: React.CSSProperties = { marginTop: 12, fontSize: 12, fontWeight: 600, color: 'var(--accent)' };
const row: React.CSSProperties = { marginTop: 14, display: 'flex', gap: 10, justifyContent: 'center' };
const applyBtn: React.CSSProperties = {
  padding: '8px 18px', borderRadius: 999,
  border: '1px solid oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h) / 0.5)',
  background: 'oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h) / 0.18)',
  color: color.ink, fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: font.sans,
};
const cancelBtn: React.CSSProperties = {
  marginTop: 12, padding: '8px 16px', borderRadius: 999,
  border: '1px solid oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h) / 0.2)',
  background: 'transparent', color: '#8A82B0', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: font.sans,
};
